import {ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewContainerRef} from '@angular/core';
import {DataResult} from '@progress/kendo-data-query';
import {RowClassArgs} from '@progress/kendo-angular-grid';
import {DialogCloseResult, DialogService} from '@progress/kendo-angular-dialog';
import {ConfigurationService} from '../../configuration.service';
import {NotificationPopupService} from '../../../shared/notification-popup/services/notification-popup.service';
import {AuthService} from '../../../auth/auth.service';
import {
    MarketGroup,
    MarketService,
    MarketsConfigResponse,
    MarketsLastChanged,
    MarketsSavePayload,
    MarketsUnassigned
} from '../../shared/models/markets-config.model';
import {BaseMarketEditor} from '../base-market-editor';
import {
    AddServiceMarketDialogComponent,
    AvailableServiceOption
} from './add-service-market-dialog/add-service-market-dialog.component';
import {toServicesCsv} from '../shared/markets-csv.util';
import {ImportMarketsDialogComponent} from '../import-markets-dialog/import-markets-dialog.component';

interface EditableService {
    // Client-only, used for trackBy/edit-targeting - never sent to the server.
    key: string;
    // The simple_market_assignment row id, or null for a service added this session.
    id: number | null;
    service: string;
    inSchedule: boolean;
    // The service's route, from rail.service_segments - never edited, display only. null
    // for a retired service (see inSchedule) or one added this session and not yet saved.
    trainOrigin: string | null;
    trainDestination: string | null;
    departures: number | null;
}

interface EditableMarket {
    key: string;
    name: string;
    // True for a market created this session, not yet saved - drives whether deleting it or
    // renaming it needs to log anything (nothing was ever persisted under it).
    isNew: boolean;
    services: EditableService[];
}

interface MarketRow {
    isGroupRow: boolean;
    // Pinned rows are never toggled and never gain service rows - a distinct flag rather than
    // overloading isGroupRow, since the two render completely differently.
    isPinnedRow?: boolean;
    // A service row currently open for add/edit renders as an in-place panel instead of its
    // normal cells.
    isEditRow?: boolean;
    // The trailing "Show N more services..." row for a market whose service list is capped
    // to the default fold - never toggled itself, just a link to lift the cap.
    isShowMoreRow?: boolean;
    market: string;
    marketRef?: EditableMarket;
    serviceRef?: EditableService;
    // Group rows only:
    itemCount?: number;
    // Data rows only:
    service?: string;
    inSchedule?: boolean;
    trainOrigin?: string | null;
    trainDestination?: string | null;
    departures?: number | null;
    // Show-more row only:
    remainingCount?: number;
    // Pinned row only:
    covered?: number;
}

// Edit-panel working state. Adding a new service edits `service` (the RSID itself, since it
// doesn't exist yet); editing an existing one edits `targetMarket` instead (a service's only
// meaningful "edit" - there's no range/day/date to change - is which market it's in).
interface ServiceDraft {
    service: string;
    targetMarket: string;
}

@Component({
    selector: 'app-service-markets',
    templateUrl: './service-markets.component.html',
    styleUrls: ['./service-markets.component.scss']
})
export class ServiceMarketsComponent extends BaseMarketEditor implements OnInit {
    // How many of an expanded market's services show before the fold, by default - a
    // market can run to hundreds of RSIDs, and showing them all at once (per an earlier
    // change, that already stopped happening by default for the whole grid) would still
    // overwhelm the one market a user is actually working in. Raised to Infinity by
    // "Show more" or by adding a service to that market (see expandOnly/addService).
    private static readonly DEFAULT_VISIBLE_SERVICES = 6;

    @Input() initial!: MarketsConfigResponse;

    public gridData: DataResult = {data: [], total: 0};
    public marketCount = 0;
    public serviceCount = 0;
    public lastChanged: MarketsLastChanged | null = null;
    public unassigned: MarketsUnassigned | null = null;
    public searchTerm = '';
    public visibleServiceLimit = ServiceMarketsComponent.DEFAULT_VISIBLE_SERVICES;

    // Only one service is ever open for add/edit at a time, matching the ranges-mode screen.
    public draft: ServiceDraft | null = null;
    public draftError: string | null = null;

    public rowClass = (context: RowClassArgs): {[key: string]: boolean} => {
        const row = context.dataItem as MarketRow;
        return {
            'market-group-row': row.isGroupRow,
            'market-pinned-row': !!row.isPinnedRow,
            'market-edit-row': !!row.isEditRow
        };
    };

    private markets: EditableMarket[] = [];

    constructor(dialogService: DialogService,
                viewContainerRef: ViewContainerRef,
                elementRef: ElementRef,
                cdr: ChangeDetectorRef,
                authService: AuthService,
                notificationPopupSvc: NotificationPopupService,
                private configurationSvc: ConfigurationService) {
        super(dialogService, viewContainerRef, elementRef, cdr, authService, notificationPopupSvc);
    }

    // The initial fetch/load-error state lives in the parent (markets.component.ts) - by the
    // time this component exists, `initial` is already a successful response.
    ngOnInit(): void {
        this.applyResponse(this.initial);
    }

    public get marketNames(): string[] {
        return this.markets.map(market => market.name);
    }

    // A just-added/edited service's departure count is unknown until the next reload (see
    // the ranges-mode screen's identical treatment of a block's covered count) - shown as
    // an em dash rather than a blank or literal "null", which read as broken, not unknown.
    public departuresDisplay(row: MarketRow): string {
        if (row.departures == null) {
            return '—';
        }
        return `${row.departures} departure${row.departures === 1 ? '' : 's'}`;
    }

    public showMoreServices(): void {
        this.visibleServiceLimit = Infinity;
        this.rebuildRows();
        this.cdr.detectChanges();
    }

    // ---- Loading -------------------------------------------------------------------------

    private applyResponse(response: MarketsConfigResponse): void {
        this.lastChanged = response.lastChanged;
        this.unassigned = response.unassigned;
        this.markets = response.markets.map(market => this.toEditableMarket(market));

        // Collapsed by default, unlike ranges mode - a per-service tenant can have hundreds
        // of services, so opening every group at once would just be a wall of rows. The
        // first market starts open (there's always something to look at without an extra
        // click), the rest wait for the user to pick one - see expandOnly for why only ever
        // one at a time.
        this.expandedMarkets = new Set<string>(this.markets.length ? [this.markets[0].name] : []);
        this.visibleServiceLimit = ServiceMarketsComponent.DEFAULT_VISIBLE_SERVICES;
        this.resetPendingChanges();
        this.editingKey = null;
        this.draft = null;
        this.draftError = null;
        this.searchTerm = '';
        this.marketCount = response.markets.length;
        this.serviceCount = response.markets.reduce((total, market) => total + (market.services?.length ?? 0), 0);
        this.rebuildRows();
    }

    private toEditableMarket(market: MarketGroup): EditableMarket {
        return {
            key: this.newKey(),
            name: market.name,
            isNew: false,
            services: (market.services ?? []).map(service => this.toEditableService(service))
        };
    }

    private toEditableService(service: MarketService): EditableService {
        return {
            key: this.newKey(), id: service.id, service: service.service, inSchedule: service.inSchedule,
            trainOrigin: service.trainOrigin, trainDestination: service.trainDestination, departures: service.departures
        };
    }

    // ---- Toggling / search --------------------------------------------------------------

    public toggleMarket(market: string): void {
        if (this.expandedMarkets.has(market)) {
            this.expandedMarkets.delete(market);
        } else {
            this.expandOnly(market);
        }
        this.rebuildRows();
        this.cdr.detectChanges();
    }

    // Only one market's services are ever shown open at once - opening a different one
    // replaces whichever was open, rather than piling up. Also resets the visible-service
    // fold back to the default, so a market that was previously "shown all" doesn't stay
    // that way the next time it's opened.
    private expandOnly(market: string): void {
        this.expandedMarkets.clear();
        this.expandedMarkets.add(market);
        this.visibleServiceLimit = ServiceMarketsComponent.DEFAULT_VISIBLE_SERVICES;
    }

    public onSearchChange(): void {
        this.rebuildRows();
        this.cdr.detectChanges();
    }

    // ---- Market-level actions --------------------------------------------------------------

    // Unlike ranges mode (a bare name prompt, via the shared promptForMarketName), this opens
    // a dedicated dialog that also offers pulling specific services in from other markets -
    // typing RSIDs one at a time right after naming a market was exactly the pain point.
    public addMarket(): void {
        const lists = this.buildServicePickerLists(null);

        const dialogRef = this.dialogService.open({
            title: 'Add market',
            content: AddServiceMarketDialogComponent,
            appendTo: this.viewContainerRef,
            width: 520
        });
        const instance = dialogRef.content.instance as AddServiceMarketDialogComponent;
        instance.existingNames = this.markets.map(m => m.name);
        instance.unassignedOptions = lists.unassignedOptions;
        instance.availableServices = lists.availableServices;

        dialogRef.result.subscribe((result: any) => {
            if (result instanceof DialogCloseResult || !result.confirmed) {
                return;
            }
            const market: EditableMarket = {key: this.newKey(), name: result.name, isNew: true, services: []};
            this.markets.push(market);
            this.expandOnly(market.name);
            this.pushMarketCreateChange(market);
            this.marketCount = this.markets.length;

            const fromUnassigned: string[] = result.selectedUnassigned ?? [];
            const fromOtherMarkets: string[] = result.selectedFromOtherMarkets ?? [];
            if (fromUnassigned.length) {
                this.addUnassignedServicesToMarket(market, fromUnassigned);
            }
            if (fromOtherMarkets.length) {
                this.moveServicesIntoMarket(market, fromOtherMarkets);
            }

            if (fromUnassigned.length || fromOtherMarkets.length) {
                this.rebuildRows();
                this.cdr.detectChanges();
            } else {
                // Nothing pulled in - fall back to the single/multi RSID entry panel, same
                // as before, so an empty market never sits there with nothing to do next.
                this.addService(market);
            }
        });
    }

    // Same dialog as addMarket, pre-filled with the market's current name and offering the
    // same two pickers to grow its RSID list at the same time as renaming it (or without
    // renaming it at all - the name field just keeps its current value if left alone).
    public renameMarket(market: EditableMarket): void {
        const lists = this.buildServicePickerLists(market);

        const dialogRef = this.dialogService.open({
            title: 'Edit market',
            content: AddServiceMarketDialogComponent,
            appendTo: this.viewContainerRef,
            width: 520
        });
        const instance = dialogRef.content.instance as AddServiceMarketDialogComponent;
        instance.currentName = market.name;
        instance.confirmLabel = 'Save';
        instance.existingNames = this.markets.filter(m => m !== market).map(m => m.name);
        instance.unassignedOptions = lists.unassignedOptions;
        instance.availableServices = lists.availableServices;

        dialogRef.result.subscribe((result: any) => {
            if (result instanceof DialogCloseResult || !result.confirmed) {
                return;
            }
            this.renameMarketChange(market, result.name);

            const fromUnassigned: string[] = result.selectedUnassigned ?? [];
            const fromOtherMarkets: string[] = result.selectedFromOtherMarkets ?? [];
            if (fromUnassigned.length) {
                this.addUnassignedServicesToMarket(market, fromUnassigned);
            }
            if (fromOtherMarkets.length) {
                this.moveServicesIntoMarket(market, fromOtherMarkets);
            }

            // Brings the edited market into focus so any services just moved/added into it
            // are actually visible, matching addMarket's own behaviour.
            this.expandOnly(market.name);
            this.rebuildRows();
            this.cdr.detectChanges();
        });
    }

    // Shared by addMarket/renameMarket: the unassigned ("Other") pool, filtered against live
    // session state (a service already assigned earlier this session must not be offered
    // again just because the server didn't know that yet when this list was fetched), and
    // every other market's services - excludeMarket leaves a market's own services out of
    // its own "move in from elsewhere" list when editing it (nothing to move from itself).
    private buildServicePickerLists(excludeMarket: EditableMarket | null):
        {unassignedOptions: {service: string}[]; availableServices: AvailableServiceOption[]} {
        const assignedNow = new Set<string>();
        this.markets.forEach(market => market.services.forEach(service => assignedNow.add(service.service)));

        const unassignedOptions = (this.unassigned?.services ?? [])
            .filter(rsid => !assignedNow.has(rsid))
            .map(service => ({service}));

        // reduce+concat, not flatMap - this project's TS target (es2015) predates it.
        const availableServices = this.markets
            .filter(market => market !== excludeMarket)
            .reduce((options: AvailableServiceOption[], market) =>
                options.concat(market.services.map(service => ({
                    service: service.service,
                    market: market.name,
                    label: `${service.service} — ${market.name}`
                }))), []);

        return {unassignedOptions, availableServices};
    }

    // Every RSID here comes from the Other pool (see _all_active_rsids/_unassigned on the
    // API side), which is by definition drawn from currently-scheduled services - inSchedule
    // is genuinely true, not just a hopeful default, unlike a brand new typed-in RSID.
    private addUnassignedServicesToMarket(target: EditableMarket, rsids: string[]): void {
        for (const rsid of rsids) {
            const service: EditableService = {
                key: this.newKey(), id: null, service: rsid, inSchedule: true, trainOrigin: null, trainDestination: null, departures: null
            };
            target.services.push(service);
            this.recordAdd(target, service);
        }
    }

    // Moves each of the given (already-assigned) RSIDs out of whichever market currently
    // holds it and into the new one - each move is logged as a block_edit, same as the
    // single-service "reassign to a different market" edit already does.
    private moveServicesIntoMarket(target: EditableMarket, rsids: string[]): void {
        for (const rsid of rsids) {
            const source = this.markets.find(market => market !== target && market.services.some(s => s.service === rsid));
            const service = source?.services.find(s => s.service === rsid);
            if (!source || !service) {
                continue;
            }

            const before = this.serviceSnapshot(source, service);
            source.services = source.services.filter(s => s !== service);
            target.services.push(service);
            const after = this.serviceSnapshot(target, service);

            const pendingAdd = this.pendingChanges.find(c => c.itemKey === service.key && c.action === 'block_add');
            if (pendingAdd) {
                pendingAdd.market = target.name;
                pendingAdd.after = after;
            } else {
                this.pendingChanges.push({action: 'block_edit', market: target.name, before, after, itemKey: service.key});
            }
        }
    }

    public deleteMarket(market: EditableMarket): void {
        const message = market.services.length
            ? `Delete market "${market.name}" and unassign all ${market.services.length} of its services?`
            : `Delete market "${market.name}"?`;
        this.confirm(message).subscribe(confirmed => {
            if (!confirmed) {
                return;
            }
            if (this.editingKey && market.services.some(service => service.key === this.editingKey)) {
                this.editingKey = null;
                this.draft = null;
            }

            this.deleteMarketChange(market, market.services.map(service => this.serviceSnapshot(market, service)));
            this.markets = this.markets.filter(m => m !== market);
            this.marketCount = this.markets.length;
            this.rebuildRows();
            this.cdr.detectChanges();
        });
    }

    // ---- Import / Export -------------------------------------------------------------------

    public importMarkets(): void {
        const dialogRef = this.dialogService.open({
            title: 'Import markets',
            content: ImportMarketsDialogComponent,
            appendTo: this.viewContainerRef,
            width: 640
        });
        const instance = dialogRef.content.instance as ImportMarketsDialogComponent;
        instance.mode = 'services';

        dialogRef.result.subscribe((result: any) => {
            if (result instanceof DialogCloseResult || !result.confirmed) {
                return;
            }
            this.applyImport(result.groups as MarketGroup[]);
        });
    }

    // Full replace: every imported market/service is treated as new from this session's
    // perspective (fresh id: null), matching how the real save already re-creates every row
    // with a fresh DB id regardless of what id it had before. Discards whatever was staged
    // pre-import - the popup's own warning says as much.
    private applyImport(groups: MarketGroup[]): void {
        this.markets = groups.map(group => this.toEditableMarket(group));
        this.markets.forEach(market => {
            market.isNew = true;
            market.services.forEach(service => {
                service.id = null;
            });
        });

        // 'external_edit' - a bulk change from outside the normal one-service-at-a-time flow,
        // not decomposed into per-service block_add entries (see market_config_audit's action
        // CHECK constraint, alembic revision 135).
        this.pendingChanges = this.markets.map(market => ({
            action: 'external_edit',
            market: market.name,
            before: null,
            after: {name: market.name, services: market.services.map(service => this.serviceSnapshot(market, service))}
        }));

        // Same collapsed-except-first default applyResponse itself uses on initial load.
        this.expandedMarkets = new Set<string>(this.markets.length ? [this.markets[0].name] : []);
        this.visibleServiceLimit = ServiceMarketsComponent.DEFAULT_VISIBLE_SERVICES;
        this.editingKey = null;
        this.draft = null;
        this.draftError = null;
        this.searchTerm = '';
        this.marketCount = this.markets.length;
        this.serviceCount = this.markets.reduce((total, market) => total + market.services.length, 0);
        this.rebuildRows();
        this.cdr.detectChanges();
        this.notifySuccess(`Imported ${this.markets.length} market${this.markets.length === 1 ? '' : 's'}. Not yet saved.`);
    }

    // Current on-screen state, unsaved edits included - deliberately not what the server has,
    // this is a pure client-side operation with no API round-trip.
    public exportMarkets(): void {
        const markets = this.markets.map(market => ({
            name: market.name,
            services: market.services
                .filter(service => service.service)
                .map(service => ({
                    id: service.id,
                    service: service.service,
                    inSchedule: service.inSchedule,
                    trainOrigin: service.trainOrigin,
                    trainDestination: service.trainDestination,
                    departures: service.departures
                }))
        }));
        this.downloadCsv(toServicesCsv(markets), 'simple_market_assignment.csv');
    }

    // ---- Service-level actions --------------------------------------------------------------

    public addService(market: EditableMarket): void {
        const service: EditableService = {
            key: this.newKey(), id: null, service: '', inSchedule: true, trainOrigin: null, trainDestination: null, departures: null
        };
        market.services.push(service);
        this.expandOnly(market.name);
        // Actively adding to this market - show its whole list rather than just the fold,
        // since the point of opening the editor here is to see everything already in it.
        this.visibleServiceLimit = Infinity;
        this.startEdit(market, service);
    }

    public editService(market: EditableMarket, service: EditableService): void {
        this.startEdit(market, service);
    }

    public deleteService(market: EditableMarket, service: EditableService): void {
        this.confirm(`Remove "${service.service}" from "${market.name}"?`).subscribe(confirmed => {
            if (!confirmed) {
                return;
            }
            if (this.editingKey === service.key) {
                this.editingKey = null;
                this.draft = null;
            }
            market.services = market.services.filter(s => s !== service);

            const pendingAdd = this.pendingChanges.find(c => c.itemKey === service.key && c.action === 'block_add');
            if (pendingAdd) {
                // Never saved in the first place - nothing to log.
                this.pendingChanges = this.pendingChanges.filter(c => c !== pendingAdd);
            } else {
                this.pendingChanges = this.pendingChanges.filter(c => c.itemKey !== service.key);
                this.pendingChanges.push({
                    action: 'block_delete', market: market.name, before: this.serviceSnapshot(market, service), after: null, itemKey: service.key
                });
            }

            this.rebuildRows();
            this.cdr.detectChanges();
        });
    }

    private startEdit(market: EditableMarket, service: EditableService): void {
        this.editingKey = service.key;
        this.draftError = null;
        this.draft = {service: service.service, targetMarket: market.name};
        this.rebuildRows();
        this.cdr.detectChanges();
        this.scrollToEditingRow();
    }

    public cancelEdit(market: EditableMarket, service: EditableService): void {
        // A just-added service that was never committed leaves nothing worth keeping around.
        if (!service.service && !service.id) {
            market.services = market.services.filter(s => s !== service);
        }
        this.editingKey = null;
        this.draft = null;
        this.draftError = null;
        this.rebuildRows();
        this.cdr.detectChanges();
    }

    public commitEdit(market: EditableMarket, service: EditableService): void {
        const draft = this.draft;
        if (!draft) {
            return;
        }

        if (!service.id) {
            this.commitAdd(market, service, draft);
        } else {
            this.commitExistingEdit(market, service, draft);
        }
    }

    // Accepts one or many RSIDs at once - comma, pipe, tab or newline separated, so a
    // straight paste from an Excel column (or row) works without reformatting first.
    // Typing one-by-one was the whole complaint; this is the fix, not a special case of it.
    private commitAdd(market: EditableMarket, service: EditableService, draft: ServiceDraft): void {
        const tokens = Array.from(new Set(this.parseRsidList(draft.service)));
        if (!tokens.length) {
            this.draftError = 'Enter at least one service (RSID).';
            return;
        }

        const valid = tokens.filter(token => !this.isDuplicate(token, service));
        const skipped = tokens.filter(token => this.isDuplicate(token, service));
        if (!valid.length) {
            this.draftError = `Already assigned elsewhere: ${this.describeSkipped(skipped, service)}.`;
            return;
        }

        // The placeholder row addService() already created becomes the first RSID; every
        // additional one gets its own new row alongside it.
        service.service = valid[0];
        this.recordAdd(market, service);
        for (const rsid of valid.slice(1)) {
            const extra: EditableService = {
                key: this.newKey(), id: null, service: rsid, inSchedule: true, trainOrigin: null, trainDestination: null, departures: null
            };
            market.services.push(extra);
            this.recordAdd(market, extra);
        }

        if (skipped.length) {
            this.notifySuccess(`Added ${valid.length}. Already assigned elsewhere, skipped: ${this.describeSkipped(skipped, service)}.`);
        }
        this.finishEdit();
    }

    // "SE207300 (VIC - MDE - AFK)" - names the market a skipped RSID is currently in, not
    // just that it's taken, so the user doesn't have to go searching for where it went.
    private describeSkipped(rsids: string[], excluding: EditableService): string {
        return rsids.map(rsid => {
            const market = this.findAssignedMarket(rsid, excluding);
            return market ? `${rsid} (${market})` : rsid;
        }).join(', ');
    }

    private recordAdd(market: EditableMarket, service: EditableService): void {
        const after = this.serviceSnapshot(market, service);
        const pendingAdd = this.pendingChanges.find(c => c.itemKey === service.key && c.action === 'block_add');
        if (pendingAdd) {
            // Re-editing a service added this session, before it's ever been saved - update
            // the still-pending add rather than logging a separate edit on top of it.
            pendingAdd.after = after;
        } else {
            this.pendingChanges.push({action: 'block_add', market: market.name, before: null, after, itemKey: service.key});
        }
    }

    // Splits on any run of comma/pipe/tab/newline/space - covers a typed comma list, a
    // pasted Excel column (newline-separated) and a pasted Excel row (tab-separated) alike.
    private parseRsidList(raw: string): string[] {
        return raw
            .split(/[,|\t\r\n ]+/)
            .map(token => token.trim())
            .filter(token => token.length > 0);
    }

    // The one existing service's edit covers both its RSID (a typo fix, or reassigning the
    // identity entirely) and which market it's in, together - there's nothing else to edit.
    private commitExistingEdit(market: EditableMarket, service: EditableService, draft: ServiceDraft): void {
        const value = draft.service.trim();
        if (!value) {
            this.draftError = 'Enter a service (RSID).';
            return;
        }
        if (value !== service.service) {
            const conflict = this.findAssignedMarket(value, service);
            if (conflict) {
                this.draftError = `"${value}" is already assigned to "${conflict}".`;
                return;
            }
        }

        const targetName = draft.targetMarket;
        const target = targetName === market.name ? market : this.markets.find(m => m.name === targetName);
        if (!target) {
            this.draftError = 'Select a market.';
            return;
        }

        const before = this.serviceSnapshot(market, service);
        service.service = value;
        if (target !== market) {
            market.services = market.services.filter(s => s !== service);
            target.services.push(service);
        }
        const after = this.serviceSnapshot(target, service);

        if (JSON.stringify(before) !== JSON.stringify(after)) {
            const pendingAdd = this.pendingChanges.find(c => c.itemKey === service.key && c.action === 'block_add');
            if (pendingAdd) {
                pendingAdd.market = target.name;
                pendingAdd.after = after;
            } else {
                this.pendingChanges.push({action: 'block_edit', market: target.name, before, after, itemKey: service.key});
            }
        }

        this.finishEdit();
    }

    private finishEdit(): void {
        this.editingKey = null;
        this.draft = null;
        this.draftError = null;
        this.rebuildRows();
        this.cdr.detectChanges();
    }

    private serviceSnapshot(market: EditableMarket, service: EditableService): {service: string; market: string} {
        return {service: service.service, market: market.name};
    }

    // Client-side only, for immediate feedback while adding - the server independently
    // re-validates the whole document on save regardless of what this finds.
    private isDuplicate(value: string, excluding: EditableService): boolean {
        return this.findAssignedMarket(value, excluding) !== null;
    }

    // Which market a given RSID currently belongs to, if any - lets a "already assigned"
    // message name the market instead of leaving the user to go find it themselves.
    private findAssignedMarket(value: string, excluding: EditableService): string | null {
        for (const market of this.markets) {
            if (market.services.some(service => service !== excluding && service.service === value)) {
                return market.name;
            }
        }
        return null;
    }

    // ---- Save ------------------------------------------------------------------------------

    public save(): void {
        this.openSaveDialog('services').subscribe(result => {
            if (!result) {
                return;
            }
            this.commitSave(result.reason);
        });
    }

    private commitSave(reason: string | null): void {
        const payload: MarketsSavePayload = {
            mode: 'services',
            markets: this.markets.map(market => ({
                name: market.name,
                services: market.services
                    .filter(service => service.service)
                    .map(service => ({id: service.id, service: service.service}))
            })),
            changes: this.pendingChanges.map(({itemKey, ...change}) => change),
            reason
        };

        this.saving = true;
        this.configurationSvc.saveMarketsConfig(payload).subscribe(response => {
            this.applyResponse(response);
            this.saving = false;
            this.notifySuccess('Market configuration saved.');
            this.cdr.detectChanges();
        }, error => {
            console.error(`Error saving markets config ${error}`);
            this.saving = false;
            // The API returns {"message": "..."} on a validation failure (e.g. a duplicate
            // service) - surface that instead of a generic failure when it's there.
            this.notifyError(error?.error?.message || 'Failed to save market configuration.');
            this.cdr.detectChanges();
        });
    }

    // ---- Row building ------------------------------------------------------------------------

    private rebuildRows(): void {
        const term = this.searchTerm.trim().toLowerCase();
        const rows: MarketRow[] = [];

        for (const market of this.markets) {
            const marketMatches = !term || market.name.toLowerCase().includes(term);
            const visibleServices = marketMatches
                ? market.services
                : market.services.filter(service => service.service.toLowerCase().includes(term));

            if (term && !marketMatches && visibleServices.length === 0) {
                continue;
            }

            rows.push({isGroupRow: true, market: market.name, marketRef: market, itemCount: market.services.length});

            // Expanded if the user opened it, or a search is narrowing results (so matches are
            // actually visible) - searching never permanently changes expandedMarkets itself.
            const expanded = this.expandedMarkets.has(market.name) || !!term;
            if (!expanded) {
                continue;
            }

            // While searching, every match shows regardless of the fold - narrowing down to
            // a handful of results is the whole point, capping them again would defeat it.
            const limit = term ? visibleServices.length : this.visibleServiceLimit;
            let shown = visibleServices.slice(0, limit);
            // The row currently open for add/edit always shows, even past the fold - most
            // relevant for a just-added service, which lands at the end of the list.
            const editing = visibleServices.find(service => service.key === this.editingKey);
            if (editing && !shown.includes(editing)) {
                shown = shown.concat(editing);
            }

            for (const service of shown) {
                if (service.key === this.editingKey) {
                    rows.push({isGroupRow: false, isEditRow: true, market: market.name, marketRef: market, serviceRef: service});
                } else {
                    rows.push({
                        isGroupRow: false, market: market.name, marketRef: market, serviceRef: service,
                        service: service.service, inSchedule: service.inSchedule,
                        trainOrigin: service.trainOrigin, trainDestination: service.trainDestination,
                        departures: service.departures
                    });
                }
            }

            const remaining = visibleServices.length - shown.length;
            if (remaining > 0) {
                rows.push({isGroupRow: false, isShowMoreRow: true, market: market.name, marketRef: market, remainingCount: remaining});
            }
        }

        // Always last, never toggled or filtered: "Everything else -> Other" is a standalone
        // figure, not a market with services of its own to expand or edit.
        if (this.unassigned) {
            rows.push({isGroupRow: false, isPinnedRow: true, market: this.unassigned.name, covered: this.unassigned.count});
        }

        this.gridData = {data: rows, total: rows.length};
    }
}
